import { useMemo } from 'react';

import {
  Box,
  ButtonBase,
  Checkbox,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@material-ui/core';

import { RiArrowLeftSLine } from '@remixicon/react';

import { SimpleAutocomplete } from '../SimpleAutocomplete';
import { GroupedAutocompleteList } from './GroupedAutocompleteList';
import { useGroupedAutocompleteContext } from './context';
import { useStyles } from './styles';
import type { GroupedAutocompleteItem } from './types';

type GroupedAutocompleteItemsProps = {
  items: GroupedAutocompleteItem[];
  loading?: boolean;
  searchPlaceholder?: string;
  emptyMessage?: string;
  // Keep items in the given order instead of floating checked ones to the
  // top. Useful when the first item is a meaningful default.
  preserveItemOrder?: boolean;
};

export const GroupedAutocompleteItems = ({
  items,
  loading,
  searchPlaceholder = 'Search...',
  emptyMessage = 'No items available',
  preserveItemOrder = false,
}: GroupedAutocompleteItemsProps) => {
  const {
    activeGroup,
    onActiveGroupChange,
    selectedItems,
    onSelectedItemsChange,
    groupItemsRef,
  } = useGroupedAutocompleteContext();
  const classes = useStyles();

  const activeGroupLabel = useMemo(() => {
    if (!activeGroup) return undefined;
    return groupItemsRef.current.find(g => g.value === activeGroup)?.label;
  }, [activeGroup, groupItemsRef]);

  const activeGroupSelection = useMemo(
    () => (activeGroup ? (selectedItems[activeGroup] ?? []) : []),
    [selectedItems, activeGroup],
  );

  const sortedItems = useMemo(() => {
    if (!activeGroup || preserveItemOrder) return items;
    const selectionSet = new Set(activeGroupSelection);
    const checked: typeof items = [];
    const rest: typeof items = [];
    for (const item of items) {
      if (selectionSet.has(item.value)) {
        checked.push(item);
      } else {
        rest.push(item);
      }
    }
    return [...checked, ...rest];
  }, [items, activeGroup, activeGroupSelection, preserveItemOrder]);

  if (activeGroup === null) return null;

  return (
    <SimpleAutocomplete.Root
      items={sortedItems}
      multiple
      value={activeGroupSelection}
      onSelect={itemId => {
        const current = selectedItems[activeGroup] ?? [];
        const next = current.includes(itemId)
          ? current.filter(v => v !== itemId)
          : [...current, itemId];
        if (next.length > 0) {
          onSelectedItemsChange({ ...selectedItems, [activeGroup]: next });
        } else {
          const { [activeGroup]: _, ...rest } = selectedItems;
          onSelectedItemsChange(rest);
        }
        return true;
      }}
    >
      {({ getOptionProps }) => (
        <Box className={classes.root}>
          <SimpleAutocomplete.Input
            autoFocus
            placeholder={searchPlaceholder}
            margin="dense"
          />
          <ButtonBase
            className={classes.header}
            onClick={() => onActiveGroupChange(null)}
            aria-label="Back"
          >
            <RiArrowLeftSLine size={18} aria-hidden />
            <Typography variant="body2" className={classes.headerLabel} noWrap>
              {activeGroupLabel || activeGroup}
            </Typography>
          </ButtonBase>
          <GroupedAutocompleteList
            loading={loading}
            emptyMessage={emptyMessage}
            hasSourceItems={items.length > 0}
            renderItem={(item, i) => {
              const isSelected = activeGroupSelection.includes(item.value);
              return (
                <ListItem
                  button
                  dense
                  disableGutters
                  {...getOptionProps(i)}
                  selected={false}
                >
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={isSelected}
                      tabIndex={-1}
                      disableRipple
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label || item.value}
                    primaryTypographyProps={{
                      variant: 'body2',
                      noWrap: true,
                      title: item.label || item.value,
                    }}
                  />
                </ListItem>
              );
            }}
          />
        </Box>
      )}
    </SimpleAutocomplete.Root>
  );
};
